import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { capture } from "@/lib/posthog";
import {
  contractingKeys,
  dashboardKeys,
  interestKeys,
  profileItemKeys,
  searchProfileKeys,
} from "@/lib/query-keys";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import { useMarkIntroSeen } from "@/hooks/mutations/useIntroSeen";
import {
  useCreateSearchProfile,
  useUpdateSearchProfile,
} from "@/hooks/mutations/useSearchProfileMutations";
import { useUpdateInterests } from "@/hooks/mutations/useInterestMutations";
import type { SearchProfile } from "@/types/domain";
import type { QualificationFilter } from "@/components/dashboard/QualificationFilterChips";
import { markOnboardingEscaped } from "./OnboardingRedirect";

/**
 * Lo que comparten los caminos del onboarding: la línea que se configura, si ya
 * quedó guardada y cómo se sale. Cada flujo pone sus pasos encima.
 */
export function useOnboardingSession() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: beta } = useBetaInformation();
  const { activeProfile, setActiveProfileId } = useActiveProfile();
  const createProfile = useCreateSearchProfile();
  const updateProfile = useUpdateSearchProfile();
  const updateInterests = useUpdateInterests();
  const markSeen = useMarkIntroSeen();

  // La línea puede crearse aquí: se guarda para no esperar a que el provider la liste.
  const [createdProfileId, setCreatedProfileId] = useState<number | null>(null);
  const [configured, setConfigured] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // Si ya lo había visto al entrar no le toca (Onboarding lo saca). Se fija al entrar:
  // marcarlo visto a mitad de camino no lo echa.
  const [enteredSeen, setEnteredSeen] = useState<boolean | null>(null);

  useEffect(() => {
    if (beta && enteredSeen === null) setEnteredSeen(!!beta.userextrainformation_introseenat);
  }, [beta, enteredSeen]);

  /** Devuelve la línea que se configura, creándola si el usuario no tiene. */
  async function ensureProfileId(name: string): Promise<number> {
    const existing = createdProfileId ?? activeProfile?.searchprofile_id ?? null;
    if (existing != null) return existing;
    const created = await createProfile.mutateAsync({ name, deferRefresh: true });
    setCreatedProfileId(created.searchprofile_id);
    setActiveProfileId(created.searchprofile_id);
    return created.searchprofile_id;
  }

  /**
   * Guarda la línea con lo que configuró el flujo y sus recursos. Los filtros que
   * el flujo no pregunta se limpian: cualquiera que arrastre cambiaría el número
   * que le mostramos.
   */
  async function saveLine({
    name,
    data,
    resources,
  }: {
    name: string;
    data: Partial<SearchProfile>;
    resources: string[];
  }): Promise<number> {
    const id = await ensureProfileId(name);
    await updateProfile.mutateAsync({
      id,
      data: {
        searchprofile_provinceids: [],
        searchprofile_districtids: [],
        searchprofile_procuringentityids: [],
        searchprofile_date_from: null,
        searchprofile_date_to: null,
        searchprofile_statuses: [],
        searchprofile_scope: null,
        searchprofile_search: null,
        searchprofile_contratadomaxdays: null,
        searchprofile_amountmin: null,
        searchprofile_amountmax: null,
        searchprofile_keywords: [],
        searchprofile_subjects: [],
        searchprofile_onlyinterests: true,
        // Marcada apaga los recursos y es del cruce automático (Corporativo): el onboarding no la marca.
        searchprofile_checkqualification: false,
        // Siempre: el onboarding reemplaza todo lo que busca la línea.
        searchprofile_name: name,
        ...data,
      },
      deferRefresh: true,
    });
    await updateInterests.mutateAsync({
      interests: resources.map((keyword) => ({
        keyword,
        active: true,
        priceitemcategory_id: null,
      })),
      searchProfileId: id,
      deferRefresh: true,
    });
    markConfigured();
    return id;
  }

  /** La línea queda configurada: desde aquí el onboarding ya no es obligatorio. */
  function markConfigured() {
    setConfigured(true);
    markSeen.mutate();
  }

  /** Vuelca al resto de la app lo que se guardó con `deferRefresh`. */
  function refreshApp() {
    return Promise.all([
      qc.invalidateQueries({ queryKey: searchProfileKeys.all }),
      qc.invalidateQueries({ queryKey: interestKeys.all }),
      qc.invalidateQueries({ queryKey: profileItemKeys.all }),
      qc.invalidateQueries({ queryKey: contractingKeys.lists() }),
      qc.invalidateQueries({ queryKey: dashboardKeys.all }),
    ]);
  }

  /** Cierra el onboarding. Con `qualification`, el dashboard abre con ese chip puesto. */
  async function finish(segment: string | null, qualification: QualificationFilter = null) {
    setFinishing(true);
    capture("onboarding_finished", { segment });
    await refreshApp();
    navigate("/dashboard", { replace: true, state: { qualification } });
  }

  function leave(segment: string | null, step: string) {
    capture("onboarding_closed", { segment, step });
    refreshApp();
    navigate("/dashboard", { replace: true });
  }

  /** Válvula: si falla la IA o el guardado, nadie queda encerrado fuera de la app. */
  function escape(segment: string | null, step: string) {
    capture("onboarding_closed", { segment, step, escaped: true });
    markOnboardingEscaped();
    navigate("/dashboard", { replace: true });
  }

  return {
    isFree: beta?.plan?.name === "FREE",
    enteredSeen,
    configured,
    finishing,
    canLeave: configured,
    saveLine,
    finish,
    leave,
    escape,
  };
}

export type OnboardingSession = ReturnType<typeof useOnboardingSession>;
